import { Alert, Button, Card, Col, DatePicker, Form, Input, Modal, Pagination, Row, Select, Space, Table } from 'antd'
import type { SorterResult, SortOrder } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { EventReportKeyColumnDescription, EventReportValueColumnDescription, KeyFilterClause, SavedReportBean, } from 'src/api/api'
import {
    downloadCSVData,
    enumView,
    FormMode,
    lowerize,
} from 'src/util/Util'
import Filters, { Filter } from './Filters'
import reportApi from './ReportApi'
import savedReportApi from '../savedReport/SavedReportApi'
import './style.css'
import '../formSortableSelect/formSortableSelect.css'
import { FormSortableSelect } from '../formSortableSelect/FormSortableSelect'
import { useReportPolling } from './useReportPolling'
import { buildReportColumns } from './reportRequest'
import {
    calculateColumnsWidth,
    formatNumberWithGrouping,
    isIdColumn,
    reportPeriodLabels,
    toNumberIfPossible
} from './columnsHelper';

const FormItem = Form.Item
const Option = Select.Option
const KeyColumns = EventReportKeyColumnDescription.ColumnEnum
const ValueColumns = EventReportValueColumnDescription.ColumnEnum

type SortState = { columnKey: string; order?: SortOrder } | null

export const Report: React.FC<{ mode: FormMode; autoLoad?: boolean }> = ({ mode, autoLoad }) => {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()

    const [error, setError] = useState<string | undefined>(undefined)
    const [form] = Form.useForm()

    const [filters, setFilters] = useState<Filter[]>([])
    const [savedName, setSavedName] = useState<string>('')

    const [shouldAutogen, setShouldAutogen] = useState<boolean>(false)

    const normalizeKey = (v: any) => String(v ?? '').replace(/\s+/g, '').toLowerCase()

    const [sortState, setSortState] = useState<SortState>({ columnKey: normalizeKey('Day Hour'), order: 'ascend' })

    const nowDate = useMemo(() => dayjs(), [])

    const keyColumnSet = useMemo(() => new Set(Object.values(KeyColumns as any) as any[]), [])
    const valueColumnSet = useMemo(() => new Set(Object.values(ValueColumns as any) as any[]), [])

    const isKeyColumn = (col: string) => keyColumnSet.has(col as any)
    const isValueColumn = (col: string) => valueColumnSet.has(col as any)

    const requestReport = async (sorter: SorterResult<any> | undefined) => {
        
        setError(undefined)
        const values = await form.validateFields()

        const keys = (values.keys as string[]) || []
        const columns = (values.columns as string[]) || []
        const allColumns = ([] as string[]).concat(keys, columns)

        const { keyColumns, valueColumns } = buildReportColumns({
            formValues: values,
            allColumnsInOrder: allColumns,
            filters,
            sorter,
            now: nowDate,
            isKeyColumn,
            isValueColumn,
        })

        try {
            const created = await reportApi.createReport({ keyColumns, valueColumns })
            return created.data
        } catch (e) {
            const msg = await (e as any).json()
            setError(msg.error)
            return undefined
        }
    }

    const { runReport, downloadCsv, loadPage, state, setError: setPollingError, reset } = useReportPolling({
        api: reportApi,
        downloadCSVData,
        requestReport,
        pollMs: 500,
    })

    useEffect(() => {
        setPollingError(error)
    }, [error, setPollingError])

    const headerFunc = (headersToProcess?: any[]) => {
        return headersToProcess?.map((header) => {
            const key = normalizeKey(header)
            const active = sortState?.columnKey === key

            return {
                title: header,
                dataIndex: String(header).toLowerCase(),
                key,
                sorter: true,
                sortOrder: active ? sortState?.order : undefined,
                sortDirections: ['ascend', 'descend'] as SortOrder[],
                render: (value: any) => {
                    if (isIdColumn(header)) return value
                    const n = toNumberIfPossible(value)
                    return n !== null ? formatNumberWithGrouping(n) : value
                },
            }
        })
    }

    const sourceFunc = (dataToProcess?: any[], headersToProcess?: any[], totalsToProcess?: any) => {
        const source =
            dataToProcess?.map(
                (row, rowIndex) =>
                    headersToProcess?.reduce(
                        (prev, current, columnIndex) => ({
                            ...prev,
                            [current.toLowerCase() ?? '']: row[columnIndex],
                            key: rowIndex,
                        }),
                        {},
                    ) ?? {},
            ) ?? []

        totalsToProcess && source?.push(lowerize(totalsToProcess))
        return source
    }

    const renderPagination = () => {
        return state.totalLines ? (
            <Pagination current={state.currentPage} onChange={loadPage} pageSize={state.pageSize}
                total={state.totalLines} />
        ) : null
    }

    const period = Form.useWatch('period', form) as SavedReportBean.ReportPeriodEnum | undefined

    const filtersToSaved = (fs: Filter[]): Record<string, any> => {
        const out: Record<string, any> = {}
        fs.forEach((f) => {
            if (!f.type) return
            if (!f.clauseType) return
            if (!f.values || f.values.length === 0) return
            out[String(f.type)] = { clauseType: f.clauseType, values: f.values }
        })
        return out
    }

    const savedToFilters = (raw?: Record<string, any>): Filter[] => {
        if (!raw) return []
        const list: Filter[] = []
        Object.entries(raw).forEach(([k, v]) => {
            const clauseType = v.clauseType
            const values = v.values
            list.push({
                type: k as any,
                clauseType, 
                values,
                loadedData: [],
            })
        })
        return list
    }

    const buildSavePayload = async (name: string): Promise<SavedReportBean> => {
        const values = await form.validateFields()
        const keys = (values.keys as string[]) || []
        const columns = (values.columns as string[]) || []
        const reportPeriod = values.period as SavedReportBean.ReportPeriodEnum

        return {
            name,
            reportPeriod,
            startDate:
                reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && values.startDate
                    ? values.startDate.format('YYYY-MM-DD')
                    : undefined,
            endDate:
                reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && values.endDate
                    ? values.endDate.format('YYYY-MM-DD')
                    : undefined,
            dimensions: keys as any,
            metrics: columns as any,
            filters: filtersToSaved(filters),
        }
    }

    const openSaveModal = () => {
        Modal.confirm({
            title: 'Save Report',
            content: (
                <div>
                    <label htmlFor="reportTitle" style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
                        Report Title
                    </label>
                    <Input id="reportTitle" defaultValue={savedName || ''} placeholder="Enter Title"
                        autoComplete="off" />
                </div>
            ),
            okText: 'Save',
            cancelText: 'Cancel',
            onOk: async () => {
                const input = document.getElementById('reportTitle') as HTMLInputElement | null
                const title = (input?.value ?? '').trim()
                if (!title) return

                try {
                    setError(undefined)
                    if (mode === FormMode.edit && id) {
                        await savedReportApi.edit(id, await buildSavePayload(title))
                        setSavedName(title)
                    } else {
                        const created = await savedReportApi.create(await buildSavePayload(title))
                        const newId = created?.data?.id
                        setSavedName(title)

                        if (newId) {
                            navigate(`/report/${newId}/edit`)
                        }
                    }
                } catch (e: any) {
                    setError(e?.error ?? 'Failed to save report')
                }
            },
        })
    }

    const rawColumns = useMemo(() => headerFunc(state.headers) ?? [], [state.headers, sortState])
    const rawSource = useMemo(() => sourceFunc(state.data, state.headers, state.totals) ?? [], [state.data, state.headers, state.totals])

    const { tableWidth } = useMemo(() => {
        return calculateColumnsWidth(rawColumns, rawSource, 500)
    }, [rawColumns, rawSource])

    useEffect(() => {
        const idNum = id ? Number(id) : undefined
        if (!idNum || Number.isNaN(idNum)) return

        const shouldLoadSaved = mode === FormMode.edit || mode === FormMode.copy || !!autoLoad
        if (!shouldLoadSaved) return

        const load = async () => {
            try {
                setError(undefined)

                const resp = await savedReportApi.view(idNum)
                const bean = resp.data as SavedReportBean

                setSavedName(bean?.name ?? '')

                form.setFieldsValue({
                    period: bean.reportPeriod,
                    startDate:
                        bean.reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && bean.startDate
                            ? dayjs(bean.startDate, 'YYYY-MM-DD')
                            : undefined,
                    endDate:
                        bean.reportPeriod === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && bean.endDate
                            ? dayjs(bean.endDate, 'YYYY-MM-DD')
                            : undefined,
                    keys: (bean.dimensions as unknown as string[]) ?? [],
                    columns: (bean.metrics as unknown as string[]) ?? [],
                })

                setFilters(savedToFilters(bean.filters as any))

                if (autoLoad) setShouldAutogen(true)
            } catch (e: any) {
                setError(e?.error ?? 'Failed to load saved report')
            }
        }

        load()
    }, [id, mode, autoLoad])

    useEffect(() => {
        if (mode !== FormMode.create) return

        form.resetFields()
        setSavedName('')
        setFilters([])
        setError(undefined)

        reset()

        setSortState({ columnKey: 'dayhour', order: 'ascend' })

        form.setFieldsValue({
            period: SavedReportBean.ReportPeriodEnum.TODAY,
            keys: [],
            columns: [],
            startDate: undefined,
            endDate: undefined,
        })
    }, [mode, form, reset])

    useEffect(() => {
        if (!shouldAutogen) return
        setShouldAutogen(false)
        runReport(undefined)
    }, [shouldAutogen])

    const keyOptions = useMemo(
        () =>
            Object.keys(KeyColumns)
                .sort()
                .map((key) => ({
                    label: enumView(key),
                    value: key,
                })),
        [],
    )

    const metricOptions = useMemo(
        () =>
            Object.keys(ValueColumns)
                .sort()
                .map((value) => ({
                    label: enumView(value),
                    value,
                })),
        [],
    )

    return (
        <div>
            <Form
                style={{ maxWidth: '1000px', margin: '0 auto' }}
                onFinish={() => runReport(undefined)}
                form={form}
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 21 }}
            >
                {error && <Alert message={error} type="error" />}

                <Card size="small" title={savedName || 'Report Builder'} className="form-card">
                    <FormItem label="Period" name="period" rules={[{ required: true, message: 'Select period' }]}>
                        <Select
                            allowClear
                            placeholder="Select period"
                            value={period}
                            onChange={(v) => form.setFieldValue('period', v)}
                        >
                            {Object.values(SavedReportBean.ReportPeriodEnum)
                                .filter((v) => v in reportPeriodLabels)
                                .map((p) => (
                                    <Option key={p} value={p} title={reportPeriodLabels[p] ?? p}>
                                        {reportPeriodLabels[p] ?? p}
                                    </Option>
                                ))}
                        </Select>
                    </FormItem>

                    {period === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && (
                        <FormItem label=" " colon={false}>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 20px 1fr',
                                    columnGap: '10px',
                                }}
                            >
                                <Form.Item
                                    name="startDate"
                                    style={{ marginBottom: 0 }}
                                    dependencies={['period', 'endDate']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue('period') !== SavedReportBean.ReportPeriodEnum.CUSTOMRANGE) {
                                                    return Promise.resolve()
                                                }
                                                return value ? Promise.resolve() : Promise.reject(new Error('Please select start date'))
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        placeholder="Start Date"
                                        style={{ width: '100%' }}
                                        disabledDate={(date) =>
                                            !!form.getFieldValue('endDate') && date.isAfter(form.getFieldValue('endDate'), 'day')
                                        }
                                    />
                                </Form.Item>

                                <span style={{ display: 'flex', justifyContent: 'center', lineHeight: '32px' }}>-</span>

                                <Form.Item
                                    name="endDate"
                                    style={{ marginBottom: 0 }}
                                    dependencies={['period', 'startDate']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue('period') !== SavedReportBean.ReportPeriodEnum.CUSTOMRANGE) {
                                                    return Promise.resolve()
                                                }
                                                return value ? Promise.resolve() : Promise.reject(new Error('Please select end date'))
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        placeholder="End Date"
                                        style={{ width: '100%' }}
                                        disabledDate={(date) =>
                                            !!form.getFieldValue('startDate') && date.isBefore(form.getFieldValue('startDate'), 'day')
                                        }
                                    />
                                </Form.Item>
                            </div>
                        </FormItem>
                    )}

                    <FormItem name="keys" label="Dimensions">
                        <FormSortableSelect
                            value={(Form.useWatch('keys', form) as string[]) ?? []}
                            onChange={(v) => form.setFieldsValue({ keys: v })}
                            placeholder="Select dimensions"
                            options={keyOptions}
                            selectProps={{ showSearch: true }}
                        />
                    </FormItem>

                    <FormItem
                        name="columns"
                        label="Metrics"
                        rules={[{ required: true, message: 'Select at least one metric' }]}
                    >
                        <FormSortableSelect
                            value={(Form.useWatch('columns', form) as string[]) ?? []}
                            onChange={(v) => form.setFieldsValue({ columns: v })}
                            placeholder="Select metrics"
                            options={metricOptions}
                            selectProps={{ showSearch: true }}
                        />
                    </FormItem>

                    <Filters filters={filters} setFilters={setFilters} />

                    <FormItem wrapperCol={{ offset: 3, span: 21 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Space>
                                <Button type="primary" htmlType="submit" loading={state.loading}>
                                    Generate report
                                </Button>
                                <Button type="default" htmlType="button" loading={state.loading} onClick={downloadCsv}>
                                    Download CSV
                                </Button>
                            </Space>

                            <Button type="primary" htmlType="button" loading={state.loading} onClick={openSaveModal}>
                                Save Report
                            </Button>
                        </div>
                    </FormItem>
                </Card>
            </Form>

            {state.data && (
                <Table
                    pagination={false}
                    columns={headerFunc(state.headers)}
                    dataSource={sourceFunc(state.data, state.headers, state.totals)}
                    rowKey="key"
                    scroll={{ x: tableWidth }}
                    onChange={(p, f, sorter: SorterResult<any>) => {

                        const incomingKeyRaw = (sorter as any).columnKey ?? (sorter as any).field
                        const incomingKey = typeof incomingKeyRaw === 'string' ? normalizeKey(incomingKeyRaw) : ''
                        const effectiveKey = incomingKey || sortState?.columnKey || 'dayhour'

                        const nextOrder = sorter.order as SortOrder | undefined

                        setSortState(effectiveKey ? { columnKey: effectiveKey, order: nextOrder } : null)

                        if (!nextOrder) {
                            runReport(undefined)
                            return
                        }

                        const headerForTitle = (state.headers ?? []).find((h) => normalizeKey(h) === effectiveKey)
                        const nextSorter: SorterResult<any> = {
                            ...sorter,
                            order: nextOrder,
                            column: (sorter as any).column ?? (headerForTitle ? ({ title: headerForTitle } as any) : undefined),
                        } as any

                        runReport(nextSorter)
                    }}
                />
            )}

            <Row>
                <Col span={24} style={{ display: 'flex', justifyContent: 'center', marginTop: '1%' }}>
                    {renderPagination()}
                </Col>
            </Row>
        </div>
    )
}


// WEBPACK FOOTER //
// ./src/component/app/report/Report.tsx